import "@styles/globals.css";
import FooterNav from "@modules/layout/footer-nav";
import Nav from "@modules/layout/nav";
import Providers from "@modules/providers";
import type { AppProps } from "next/app";
import Script from "next/script";
import { useRouter } from "next/router";
import { useEffect } from "react";

export default function App({ Component, pageProps }: AppProps) {
  const metaPixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID || "1161136935834352";
  const GA_ID = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID || "G-H5LQ24YNB3";
  const CLARITY_ID = process.env.NEXT_PUBLIC_CLARITY_ID || "tji0nfgtvx";

  const { locale } = useRouter();
  const isArabic = locale === "ar";

  useEffect(() => {
    const lang = isArabic ? "ar" : "en";
    const dir = isArabic ? "rtl" : "ltr";
    if (document.documentElement.lang !== lang) {
      document.documentElement.lang = lang;
    }
    if (document.documentElement.dir !== dir) {
      document.documentElement.dir = dir;
    }
  }, [isArabic]);

  return (
    <Providers>
      {/* Facebook Pixel */}
      {metaPixelId && (
        <>
          <Script id="meta-pixel" strategy="lazyOnload">
            {`
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window,document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${metaPixelId}');
              fbq('track', 'PageView');
            `}
          </Script>

          <noscript>
            <img
              height="1"
              width="1"
              style={{ display: "none" }}
              src={`https://www.facebook.com/tr?id=${metaPixelId}&ev=PageView&noscript=1`}
            />
          </noscript>
        </>
      )}

      {/* Google Analytics */}
      {GA_ID && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
            strategy="lazyOnload"
          />
          <Script id="ga-script" strategy="lazyOnload">
            {`
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${GA_ID}');
            `}
          </Script>
        </>
      )}

      {/* Microsoft Clarity */}
      {CLARITY_ID && (
        <Script id="clarity-script" strategy="lazyOnload">
          {`
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${CLARITY_ID}");
          `}
        </Script>
      )}

      <link
        rel="preload"
        href="/fonts/Massilia-Medium.woff2"
        as="font"
        type="font/woff2"
        crossOrigin=""
      />

      {isArabic && (
        <>
          <link
            rel="preload"
            href="/fonts/GE_SS_Two_Medium.otf"
            as="font"
            type="font/otf"
            crossOrigin=""
          />
          <link
            rel="preload"
            href="/fonts/GE_SS_Two_Bold.otf"
            as="font"
            type="font/otf"
            crossOrigin=""
          />
        </>
      )}

      <Nav />
      <Component {...pageProps} />
      <FooterNav />
    </Providers>
  );
}
